/**
 * Types for the "Filing check" tab on /docuware-invoices.
 *
 * The tab runs the opposite direction to the other two: it starts from a Business Central
 * posting and asks whether the invoice was ever filed in DocuWare. The four statuses are the
 * contract with the API and are never collapsed in the UI — "no document anywhere", "the
 * document exists but is not linked" and "this is a normal fragment of an already-filed
 * collective invoice" (P3) are different problems, with different owners, and only one of them
 * is a defect worth chasing.
 */

export type FilingStatus = 'missing' | 'unlinked' | 'collective' | 'nokey';

export interface FilingCheckRow {
    companyCode: string;
    /** The posted BC purchase invoice number. Identity of the row. */
    bcDocumentNo: string;
    postingDate: string | null;
    documentDate: string | null;
    vendorNo: string | null;
    vendorName: string | null;
    vendorInvoiceNo: string | null;
    orderNo: string | null;
    currency: string | null;
    netAmount: number | null;
    grossAmount: number | null;
    status: FilingStatus;
    /** Free samples, corrections and reversals. Flagged, never hidden by default. */
    zeroAmount: boolean;
    /**
     * How the DocuWare candidate was found, when there is one:
     *   'exact'       the invoice numbers are identical
     *   'normalised'  identical after folding case and separators
     *   'contains'    one number contains the other — the "KORREKTUR <no>" / "<no>
     *                 RESTLIEFERUNG" case. Weaker evidence, shown as such.
     */
    matchBasis: 'exact' | 'normalised' | 'contains' | null;
    dwDocumentId: number | null;
    docId: number | null;
    fileCabinetId: string | null;
    dwSupplier: string | null;
    /** The candidate's OWN invoice number and amount, so a loose match can be rejected on sight. */
    dwInvoiceNumber: string | null;
    dwAmount: number | null;
    dwCurrency: string | null;
    dwStoredAt: string | null;
    /** Where the candidate document IS linked. Non-null = the link points somewhere else. */
    dwBcDocumentNo: string | null;
    /**
     * How many postings the candidate document already carries.
     *
     * Load-bearing before a hand link, not after: from the SECOND posting on, the order check
     * treats the document as a collective invoice and stops reconciling it against the booked
     * invoice (po-check/run.ts). Linking here therefore changes what the Order Check tab says
     * about that document, and the dialog says so before the reviewer commits rather than
     * leaving it to be discovered.
     */
    dwPostingCount: number | null;
    documentUrl: string | null;
    documentUrlVerified: boolean;
    baseUrl: string | null;

    /**
     * The last thing that happened to THIS posting's links (migration 194) — the visible half
     * of Entscheid 2.
     *
     * A posting can be back on this list because the matcher took a reviewer's link away after
     * matching that document to another posting on both keys. Without this the row is
     * indistinguishable from one that was never linked, and the reviewer's work disappears
     * overnight with nothing anywhere to say so.
     */
    auditAction: 'link' | 'unlink' | 'replaced' | null;
    auditDocumentId: number | null;
    auditReplacedBy: string | null;
    auditMatchMethod: string | null;
    auditLinkedBy: string | null;
    auditLinkedAt: string | null;
    auditActedBy: string | null;
    auditActedAt: string | null;
}

/** A DocuWare document offered in the link dialog's search. */
export interface FilingCheckDocument {
    documentId: number;
    companyCode: string;
    docId: number | null;
    fileCabinetId: string | null;
    invoiceNumber: string | null;
    supplier: string | null;
    amount: number | null;
    currency: string | null;
    documentType: string | null;
    invoiceCategory: string | null;
    storedAt: string | null;
    invoiceDate: string | null;
    /** DocuWare's own BELEGNUMMER, raw — a list here is what P1's splitter worked on. */
    belegnummer: string | null;
    /** The postings this document already links, primary first. */
    bcPostings: string | null;
    postingCount: number;
    documentUrl: string | null;
    documentUrlVerified: boolean;
}

/** One existing hand link, as the "Manual links" panel lists it. */
export interface FilingCheckManualLink {
    companyCode: string;
    bcDocumentNo: string;
    documentId: number;
    isPrimary: boolean;
    createdBy: string | null;
    createdAt: string | null;
    docId: number | null;
    fileCabinetId: string | null;
    invoiceNumber: string | null;
    supplier: string | null;
    amount: number | null;
    currency: string | null;
    bcPostings: string | null;
    postingCount: number;
    documentUrl: string | null;
}

export interface FilingCheckLinkPosting {
    bcDocumentNo: string;
    origin: string;
    isPrimary: boolean;
    createdBy: string | null;
}

/** What the write endpoint reports back. Every side effect is in here, none of it inferred. */
export interface FilingCheckLinkResult {
    action: 'link' | 'unlink';
    companyCode: string;
    bcDocumentNo: string;
    documentId: number;
    /** The link also became the document's primary — it had none before. */
    becamePrimary?: boolean;
    postings: FilingCheckLinkPosting[];
    /** This link is what pushed the document over one posting. */
    becameCollective?: boolean;
    collective: boolean;
    /** Documents re-checked by the order check, or null when that run failed and was logged. */
    orderCheckRefreshed: number | null;
}

export interface FilingCheckCompanyCount {
    companyCode: string;
    status: FilingStatus;
    docs: number;
    zeroDocs: number;
    /** Of those, how many rest on a containment match rather than on the number itself. */
    containsDocs: number;
}

export interface FilingCheckSummary {
    minPostingDate: string;
    companies: string[];
    /** Counted over the whole corpus, NOT through the filters — see the endpoint comment. */
    byCompany: FilingCheckCompanyCount[];
    /** Posted invoices in scope per company: the denominator the exceptions are read against. */
    postedTotals: Record<string, number>;
}

export interface FilingCheckFilters {
    companyCode: string;
    status: string;
    search: string;
    /** '' or 'nonzero'. Opt-in, because zero-amount postings are half the list. */
    amount: string;
}

export const EMPTY_FILING_CHECK_FILTERS: FilingCheckFilters = {
    companyCode: '', status: '', search: '', amount: '',
};

export type FilingCheckSortField =
    | 'status' | 'postingDate' | 'documentDate' | 'company' | 'bcDocumentNo'
    | 'vendorName' | 'vendorInvoiceNo' | 'orderNo' | 'netAmount';

export interface FilingCheckListResult {
    items: FilingCheckRow[];
    summary: FilingCheckSummary;
    total: number;
}

export const FILING_STATUS_LABELS: Record<FilingStatus, { label: string; explain: string }> = {
    missing: {
        label: 'Not in DocuWare',
        explain: 'The supplier invoice number of this posting appears nowhere in the '
            + "company's DocuWare archive — not even as part of another number. The document "
            + 'was never filed.',
    },
    unlinked: {
        label: 'Filed, not linked',
        explain: 'A DocuWare document carrying this invoice number exists — it is just not '
            + 'linked to this posting. A matching gap, not a filing gap. A row marked "loose '
            + 'match" was found by containment (e.g. filed as "KORREKTUR <number>") and is '
            + 'weaker evidence: check the found number and amount before believing it.',
    },
    collective: {
        label: 'Sammelbeleg',
        explain: 'One archived document legitimately covers several postings, and this posting '
            + 'is a normal fragment of it — not a filing gap and not a matcher error. '
            + '"dwPostingCount" on the row is how many postings the document already carries.',
    },
    nokey: {
        label: 'No invoice number',
        explain: 'The posting carries no supplier invoice number, so there is nothing to '
            + 'search DocuWare by. Not the same as "searched and found nothing".',
    },
};
